import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Progress } from './ui/progress';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { GiftRanking } from './GiftRanking';
import { AIGiftSuggestions } from './AIGiftSuggestions';
import { GiftSuggestions } from './GiftSuggestions';
import { 
  Calendar, 
  Users, 
  DollarSign, 
  Gift, 
  Clock,
  Loader2,
  AlertCircle,
  Sparkles,
  Edit,
  List,
  LayoutGrid,
  UserPlus,
  Trash2,
  ArrowUpDown
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftGroupOverviewProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  initialView?: ViewMode;
}

interface Member {
  id: string;
  user_id: string | null;
  contact: string;
  role: string;
  status: string;
  workflow_state?: string;
  contributed_amount: number;
  invited_at: string;
  accepted_at: string | null;
  workflow_state_updated_at?: string | null;
  email?: string;
  phone?: string;
}

interface Suggestion {
  id: string;
  title: string;
  description: string;
  url: string;
  price: number;
  image_url?: string;
  suggested_by_user_id: string;
  suggested_by_email?: string;
  status: string;
  created_at: string;
}

interface GiftGroupData {
  id: string;
  title: string;
  type_id: number;
  type_name: string;
  type_description: string;
  creator_id: string;
  gift_date: string;
  order_by_date: string;
  recommended_contribution: number | null;
  status: string;
  total_pool: number;
  created_at: string;
  updated_at: string;
  members: Member[];
  suggestions: Suggestion[];
  currentUserRole: string;
  currentUserStatus: string;
}

type ViewMode = 'ranking' | 'ai-suggestions' | 'manual-suggestions';
type TabValue = 'ranking' | 'members' | 'activity';

interface Activity {
  id: string;
  type: string;
  message: string;
  createdAt: string;
  userName: string;
}

export function GiftGroupOverview({ onNavigate, giftGroupId, initialView }: GiftGroupOverviewProps) {
  const [giftGroup, setGiftGroup] = useState<GiftGroupData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewMode>(initialView || 'ranking');
  const [activeTab, setActiveTab] = useState<TabValue>('ranking');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesSortAsc, setActivitiesSortAsc] = useState(false); // false = newest first (desc), true = oldest first (asc)
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [newMemberContact, setNewMemberContact] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; contact: string } | null>(null);
  const [removingMember, setRemovingMember] = useState(false);

  useEffect(() => {
    if (!giftGroupId) {
      setError('No gift group selected');
      setLoading(false);
      return;
    }

    const fetchGiftGroup = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getGiftGroup(giftGroupId);
        setGiftGroup(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load gift group';
        setError(errorMessage);
        toast.error('Failed to load gift group', {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchGiftGroup();
  }, [giftGroupId]);

  const fetchActivities = async () => {
    if (!giftGroupId) return;
    
    try {
      setActivitiesLoading(true);
      const data = await api.getGiftGroupActivities(giftGroupId);
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
      toast.error('Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivities();
    }
  }, [activeTab, giftGroupId]);

  const handleAddMember = async () => {
    if (!newMemberContact.trim() || !giftGroupId) return;

    try {
      setAddingMember(true);
      await api.addGiftGroupMember(giftGroupId, newMemberContact.trim());
      
      toast.success('Member added!', {
        description: `Invitation sent to ${newMemberContact}`,
      });

      // Refresh gift group data
      const data = await api.getGiftGroup(giftGroupId);
      setGiftGroup(data);

      // Close dialog and reset form
      setShowAddMemberDialog(false);
      setNewMemberContact('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add member';
      toast.error('Failed to add member', {
        description: errorMessage,
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!giftGroupId || !memberToRemove) return;

    try {
      setRemovingMember(true);
      await api.removeGiftGroupMember(giftGroupId, memberToRemove.id);
      
      toast.success('Member removed', {
        description: `${memberToRemove.contact} has been removed from the gift group`,
      });

      // Refresh gift group data
      const data = await api.getGiftGroup(giftGroupId);
      setGiftGroup(data);
      setMemberToRemove(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      toast.error('Failed to remove member', {
        description: errorMessage,
      });
    } finally {
      setRemovingMember(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading gift group...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !giftGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Gift group not found'}</AlertDescription>
          </Alert>
          <Button
            className="mt-4 w-full bg-primary hover:bg-primary/90"
            onClick={() => onNavigate('dashboard', { tab: 'todo' })}
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  // Calculate metrics
  const gifters = giftGroup.members.filter(m => m.role === 'gifter');
  const acceptedGifters = gifters.filter(m => m.status === 'accepted');
  const totalContributed = gifters.reduce((sum, m) => sum + m.contributed_amount, 0);
  const targetPool = (giftGroup.recommended_contribution || 0) * acceptedGifters.length;
  const percentContributed = targetPool > 0 ? (totalContributed / targetPool) * 100 : 0;

  const formatDate = (dateString: string) => {
    // Handle both ISO timestamps and YYYY-MM-DD dates
    if (!dateString) return 'N/A';
    
    // If it's an ISO timestamp (contains 'T'), convert it with date and time
    if (dateString.includes('T')) {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // Otherwise parse YYYY-MM-DD as local date, not UTC (no time for date-only strings)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="p-6 border border-primary/20 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <Badge variant="secondary">{giftGroup.type_name}</Badge>
              <h1 className="text-primary mb-0">{giftGroup.title}</h1>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>Gift Date: {formatDate(giftGroup.gift_date)}</span>
            </div>
          </div>
        </Card>

        {/* Status Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">Gift Pool Status</h3>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{formatCurrency(totalContributed)}</div>
              <Progress value={percentContributed} className="h-1.5" />
              <div className="text-xs text-muted-foreground leading-none">
                {percentContributed.toFixed(0)}% of {formatCurrency(targetPool)} target
              </div>
            </div>
          </Card>

          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">Participation</h3>
                <Users className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{acceptedGifters.length}/{gifters.length}</div>
              <Progress value={(acceptedGifters.length / gifters.length) * 100} className="h-1.5" />
              <div className="text-xs text-muted-foreground leading-none">
                {gifters.length - acceptedGifters.length} pending
              </div>
            </div>
          </Card>

          <Card className="p-3 border border-primary/20 shadow-lg">
            <div className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-xs text-muted-foreground uppercase leading-none">Gift Status</h3>
                <Gift className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="text-primary leading-none">{giftGroup.suggestions.length} suggestions</div>
              <div className="text-xs text-muted-foreground leading-none">
                Status: {giftGroup.status.replace('_', ' ').charAt(0).toUpperCase() + giftGroup.status.slice(1)}
              </div>
            </div>
          </Card>
        </div>

        {/* Info Panel - Now visible in all tabs */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="p-3 bg-blue-50 border-blue-200 border-2">
            <div className="flex flex-col gap-y-1">
              <h4 className="text-primary leading-none">How Ranking Works</h4>
              <ul className="text-sm text-muted-foreground space-y-0.5">
                <li>• Your ranking influences the group ranking</li>
                <li>• Group ranking determines purchase order</li>
                <li>• Gifts within budget are prioritized</li>
                <li>• Order by date affects when gifts are purchased</li>
              </ul>
            </div>
          </Card>

          <Card className="p-3 bg-green-50 border-green-200 border-2">
            <div className="flex flex-col gap-y-1">
              <h4 className="text-primary leading-none">Purchase Logic</h4>
              <p className="text-sm text-muted-foreground leading-snug">
                Better to Give than Receive will automatically purchase the highest group-ranked gift that's within 
                budget and can arrive by the gift date. Multiple gifts can be purchased until 
                the pool is depleted.
              </p>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabValue)} className="space-y-4">
          {/* Tabs and Action Buttons on Same Line */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'ranking' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => {
                  setActiveTab('ranking');
                  setActiveView('ranking');
                }}
              >
                <LayoutGrid className="w-4 h-4 mr-1.5" />
                Gift Rankings
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'members' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => setActiveTab('members')}
              >
                <Users className="w-4 h-4 mr-1.5" />
                Members
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`border-2 ${activeTab === 'activity' ? 'border-primary bg-primary text-white hover:bg-primary/90' : 'border-primary/30 text-primary hover:bg-primary/10'}`}
                onClick={() => setActiveTab('activity')}
              >
                <List className="w-4 h-4 mr-1.5" />
                Activity
              </Button>
            </div>

            {/* Action Buttons - Only show on Rankings tab */}
            {activeTab === 'ranking' && (
              <div className="flex items-center gap-2 flex-wrap">
                {activeView === 'ranking' ? (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                      onClick={() => setActiveView('ai-suggestions')}
                    >
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      AI Gift Ideas
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                      onClick={() => setActiveView('manual-suggestions')}
                    >
                      <Edit className="w-4 h-4 mr-1.5" />
                      Add Manual Gifts
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                      onClick={() => setActiveView('ranking')}
                    >
                      <LayoutGrid className="w-4 h-4 mr-1.5" />
                      Back to Rankings
                    </Button>
                    {activeView === 'ai-suggestions' ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                        onClick={() => setActiveView('manual-suggestions')}
                      >
                        <Edit className="w-4 h-4 mr-1.5" />
                        Add Manual Gifts
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-2 border-primary/30 text-primary hover:bg-primary/10"
                        onClick={() => setActiveView('ai-suggestions')}
                      >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        AI Gift Ideas
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Rankings Tab */}
          <TabsContent value="ranking" className="mt-0">
            <div className="space-y-4">
              
              {/* Dynamic Content Based on View */}
              {activeView === 'ranking' && (
                <GiftRanking 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                />
              )}
              
              {activeView === 'ai-suggestions' && (
                <AIGiftSuggestions 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                  onSuggestionsAdded={() => setActiveView('ranking')}
                />
              )}
              
              {activeView === 'manual-suggestions' && (
                <GiftSuggestions 
                  onNavigate={onNavigate} 
                  giftGroupId={giftGroup.id}
                  embedded={true}
                />
              )}
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20 flex items-center justify-between">
                <h2 className="text-primary">Gift Group Members</h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddMemberDialog(true)}
                    className="border-primary/30 hover:bg-primary/5"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Gifter
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name/Contact</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Contributed</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {giftGroup.members.map((member) => {
                    // Helper function to format workflow state for display
                    const formatWorkflowState = (state: string | undefined) => {
                      if (!state) return 'Pending Acceptance';
                      
                      // Map workflow states to display names
                      const stateMap: Record<string, string> = {
                        'pending_acceptance': 'Pending Acceptance',
                        'declined': 'Declined',
                        'pending_contribution': 'Pending Contribution',
                        'pending_ai_gifts': 'Pending AI Gifts',
                        'pending_manual_gifts': 'Pending Manual Gifts',
                        'pending_ranking': 'Pending Ranking',
                        'pending_order': 'Pending Order',
                        'pending_delivery': 'Pending Delivery',
                        'pending_confirmation': 'Pending Confirmation',
                        'removed': 'Removed',
                        'active': 'Active'
                      };
                      
                      return stateMap[state] || state.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    };

                    // Determine badge styling based on workflow state
                    const getStatusStyle = (workflowState: string | undefined) => {
                      if (!workflowState) return 'bg-red-100 text-red-800 border-red-300';
                      
                      switch (workflowState) {
                        case 'active':
                          return 'bg-green-100 text-green-800 border-green-300';
                        case 'pending_delivery':
                        case 'pending_confirmation':
                          return 'bg-blue-100 text-blue-800 border-blue-300';
                        case 'declined':
                        case 'removed':
                          return 'bg-red-100 text-red-800 border-red-300';
                        case 'pending_acceptance':
                          return 'bg-yellow-100 text-yellow-800 border-yellow-300';
                        case 'pending_contribution':
                          return 'bg-orange-100 text-orange-800 border-orange-300';
                        case 'pending_ai_gifts':
                        case 'pending_manual_gifts':
                          return 'bg-purple-100 text-purple-800 border-purple-300';
                        case 'pending_ranking':
                          return 'bg-indigo-100 text-indigo-800 border-indigo-300';
                        case 'pending_order':
                          return 'bg-cyan-100 text-cyan-800 border-cyan-300';
                        default:
                          return 'bg-gray-100 text-gray-800 border-gray-300';
                      }
                    };

                    return (
                      <TableRow key={member.id}>
                        <TableCell>{member.email || member.phone || member.contact}</TableCell>
                        <TableCell>
                          <Badge className={member.role === 'gifter' ? 'bg-primary/10 text-primary border-primary/30' : 'bg-secondary/50 text-secondary-foreground border-secondary'}>
                            {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {member.role === 'giftee' ? (
                            <Badge className="bg-green-100 text-green-800 border-green-300">
                              Active
                            </Badge>
                          ) : (
                            <Badge className={getStatusStyle(member.workflow_state)}>
                              {formatWorkflowState(member.workflow_state)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {member.role === 'gifter' ? formatCurrency(member.contributed_amount) : '-'}
                        </TableCell>
                        <TableCell>
                          {member.role === 'giftee' ? '-' : (
                            member.workflow_state_updated_at 
                              ? formatDate(member.workflow_state_updated_at) 
                              : (member.accepted_at ? formatDate(member.accepted_at) : 'Pending')
                          )}
                         </TableCell>
                        <TableCell className="text-right">
                          {member.contributed_amount === 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMemberToRemove({ id: member.id, contact: member.email || member.phone || member.contact })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="border border-primary/20 shadow-lg">
              <div className="p-4 border-b border-primary/20 flex items-center justify-between">
                <h2 className="text-primary">Recent Activity</h2>
                {activities.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setActivitiesSortAsc(!activitiesSortAsc)}
                    className="border-primary/30 hover:bg-primary/5 gap-2"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    {activitiesSortAsc ? 'Oldest First' : 'Newest First'}
                  </Button>
                )}
              </div>
              {activitiesLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-primary" />
                  <p className="text-muted-foreground">Loading activities...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No activity yet</p>
                  <p className="text-sm mt-1">Activity will appear here as members interact with the gift group</p>
                </div>
              ) : (
                <div className="p-4">
                  {[...activities]
                    .sort((a, b) => {
                      const dateA = new Date(a.createdAt).getTime();
                      const dateB = new Date(b.createdAt).getTime();
                      return activitiesSortAsc ? dateA - dateB : dateB - dateA;
                    })
                    .map((activity) => {
                      // Format date and time in browser's local timezone
                      console.log('Activity timestamp raw:', activity.createdAt);
                      const date = new Date(activity.createdAt);
                      console.log('Activity date object:', date);
                      console.log('Activity date ISO:', date.toISOString());
                      console.log('Activity date timezone offset:', date.getTimezoneOffset());
                      
                      const dateStr = date.toLocaleDateString(undefined, { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      });
                      const timeStr = date.toLocaleTimeString(undefined, { 
                        hour: 'numeric', 
                        minute: '2-digit', 
                        hour12: true 
                      });
                      
                      console.log('Activity formatted date:', dateStr, timeStr);
                      
                      return (
                        <div key={activity.id} className="flex items-center gap-3 py-2 border-b border-primary/10 last:border-0 text-sm">
                          <span className="text-muted-foreground whitespace-nowrap">
                            {dateStr} {timeStr}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="flex-1">{activity.message}</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Gifter</DialogTitle>
            <DialogDescription>
              Enter the email address or phone number of the person you want to invite to this gift group.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="contact">Email or Phone</Label>
              <Input
                id="contact"
                type="text"
                placeholder="gifter@example.com or +1234567890"
                value={newMemberContact}
                onChange={(e) => setNewMemberContact(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !addingMember) {
                    handleAddMember();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddMemberDialog(false);
                setNewMemberContact('');
              }}
              disabled={addingMember}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMember}
              disabled={!newMemberContact.trim() || addingMember}
              className="bg-primary hover:bg-primary/90"
            >
              {addingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Gifter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Gifter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold">{memberToRemove?.contact}</span> from this gift group?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMember}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removingMember}
            >
              {removingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}